/**
 * SearchHealthBadge - Health status indicator for the new search index system
 *
 * Displays real-time status of all search indexes (organizations, deals, products, mtb)
 * with document counts, last sync times, and overall health status.
 */

import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import StorageIcon from '@mui/icons-material/Storage'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Chip, CircularProgress, Divider, InputLabel, Stack, Tooltip, Typography } from '@mui/material'
import { IndexStats, useSearchHealth } from 'api/search/useSearchHealth'
import * as React from 'react'

type HealthStatus = 'healthy' | 'initializing' | 'error' | 'loading'

function iconFor(status: HealthStatus) {
  switch (status) {
    case 'healthy':
      return <CloudDoneIcon fontSize="small" />
    case 'initializing':
      return <WarningAmberIcon fontSize="small" />
    case 'error':
      return <ErrorOutlineIcon fontSize="small" />
    default:
      return <CircularProgress size={16} />
  }
}

function formatTimeSince(isoDate: string | null): string {
  if (!isoDate) return 'Never'
  const diff = Date.now() - new Date(isoDate).getTime()
  const seconds = Math.floor(diff / 1000)

  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatTime(isoDate: string | null): string {
  if (!isoDate) return '--'
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(isoDate))
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <InputLabel sx={{ fontSize: 12, color: 'text.secondary' }}>{children}</InputLabel>
)

const Row = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Label>{label}</Label>
    <Box sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, textAlign: 'right' }}>{value}</Box>
  </Stack>
)

interface IndexRowProps {
  name: string
  stats: IndexStats
}

const IndexRow: React.FC<IndexRowProps> = ({ name, stats }) => (
  <Row
    label={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <StorageIcon sx={{ fontSize: 14, opacity: 0.8 }} />
        <span style={{ textTransform: 'capitalize' }}>{name}</span>
      </Box>
    }
    value={
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          variant="outlined"
          color={stats.ready ? 'success' : 'warning'}
          label={`${stats.documentCount.toLocaleString()} docs`}
          sx={{ height: 20, fontSize: 11 }}
        />
        <Chip
          size="small"
          variant="outlined"
          color={stats.ready ? 'default' : 'warning'}
          label={`~${stats.memoryEstimateMB}MB`}
          sx={{ height: 20, fontSize: 11 }}
        />
      </Stack>
    }
  />
)

export default function SearchHealthBadge({ pollMs = 60_000, compact = true }: { pollMs?: number; compact?: boolean }) {
  const { data, isLoading, isError, error } = useSearchHealth(pollMs)

  if (isLoading) {
    return (
      <Chip
        size="small"
        variant="filled"
        color="default"
        icon={<CircularProgress size={16} />}
        sx={{ bgcolor: 'transparent' }}
      />
    )
  }

  if (isError || !data) {
    return (
      <Tooltip title={error?.message ?? 'Search service unavailable'}>
        {/* <Chip size="small" color="error" label="search: error" /> */}
        <SyncProblemIcon fontSize="small" sx={{ color: 'text.error' }} />
      </Tooltip>
    )
  }

  const { healthy, message, organizations, deals, products, mtb } = data

  // Determine overall status
  const status: HealthStatus = healthy ? 'healthy' : 'initializing'

  // Total document count
  const totalDocs =
    (organizations?.documentCount || 0) +
    (deals?.documentCount || 0) +
    (products?.documentCount || 0) +
    (mtb?.documentCount || 0)

  // Most recent sync time
  const syncTimes = [
    organizations?.lastSyncTime,
    deals?.lastSyncTime,
    products?.lastSyncTime,
    mtb?.lastSyncTime,
  ].filter(Boolean) as string[]

  const mostRecentSync =
    syncTimes.length > 0
      ? syncTimes.reduce((latest, current) => (new Date(current) > new Date(latest) ? current : latest))
      : null

  // Icon color based on status
  const iconColor = status === 'healthy' ? 'success.main' : status === 'initializing' ? 'warning.main' : 'error.main'

  const tip = (
    <Stack spacing={1} sx={{ minWidth: 280, maxWidth: 340 }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: iconColor,
          }}
        />
        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
          {message}
        </Typography>
      </Stack>

      <Divider flexItem />

      {/* Index stats */}
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Search Indexes
      </Typography>

      {organizations && <IndexRow name="organizations" stats={organizations} />}
      {deals && <IndexRow name="deals" stats={deals} />}
      {products && <IndexRow name="products" stats={products} />}
      {mtb && <IndexRow name="mtb" stats={mtb} />}

      <Divider flexItem />

      {/* Summary */}
      <Row
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StorageIcon sx={{ fontSize: 14, opacity: 0.8 }} />
            Total
          </Box>
        }
        value={`${totalDocs.toLocaleString()} documents`}
      />

      <Row
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.8 }} />
            Last Sync
          </Box>
        }
        value={
          <span>
            {formatTimeSince(mostRecentSync)} {mostRecentSync && `(${formatTime(mostRecentSync)})`}
          </span>
        }
      />
    </Stack>
  )

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip
        title={tip}
        arrow
        placement="bottom"
        slotProps={{
          popper: {
            modifiers: [
              { name: 'preventOverflow', options: { altAxis: true, tether: true, rootBoundary: 'viewport' } },
            ],
          },
          tooltip: {
            sx: {
              maxWidth: 380,
              p: 1.25,
              '& *': { wordBreak: 'break-word' },
            },
          },
        }}
      >
        <Chip
          size="small"
          variant="filled"
          color="default"
          icon={iconFor(status)}
          label={compact ? undefined : `${totalDocs.toLocaleString()} docs`}
          aria-label={`search ${status} — ${totalDocs} documents indexed`}
          sx={{
            bgcolor: 'transparent',
            px: compact ? 0.5 : undefined,
            '& .MuiChip-icon': { color: iconColor },
          }}
        />
      </Tooltip>
    </Stack>
  )
}
